import { DateTime } from '@ksyos/date-time';
import { uniqBy } from 'lodash';
import type { EpisodeCategory } from '@common/enums';
import type { CompensationPayoutForServiceActivity } from '@common/validation/compensationPayoutForServiceActivity';
import type {
    IProvidedServicesAndCompensationPayouts,
    IServiceOrganizationSettings,
} from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { ServiceActivityCompensationPayout } from './ServiceActivityCompensationPayout';

type ServiceActivityCompensationPayoutsProps = {
    compensationPayouts: CompensationPayoutForServiceActivity[];
    employmentId?: string;
    episodeCategory: EpisodeCategory;
    higherLevelCompensationPayouts?: IProvidedServicesAndCompensationPayouts;
    onSubmit: () => void;
    organizationId: string;
    serviceEmployments: IProvidedServicesAndCompensationPayouts['serviceEmployments'];
    serviceOrganizationSettings: IServiceOrganizationSettings[];
};

export function ServiceActivityCompensationPayouts(props: Readonly<ServiceActivityCompensationPayoutsProps>) {
    const translate = getLanguage();

    const {
        compensationPayouts,
        employmentId,
        episodeCategory,
        higherLevelCompensationPayouts,
        onSubmit,
        organizationId,
        serviceEmployments,
        serviceOrganizationSettings,
    } = props;

    return (
        <>
            {employmentId &&
                serviceEmployments.map((serviceEmployment) => {
                    return serviceEmployment.serviceActivities.map((serviceActivity) => {
                        const title = `${serviceActivity.name} (${DateTime.from(serviceEmployment.startDate).format(
                            'DD-MM-YYYY'
                        )} - ${serviceEmployment.endDate ? DateTime.from(serviceEmployment.endDate).format('DD-MM-YYYY') : translate.common.now.toLowerCase()})`;
                        const canEdit = hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit);
                        const compensationPayoutsForActivity = compensationPayouts.filter(
                            (compensationPayout) => compensationPayout.serviceActivityId === serviceActivity.id
                        );

                        let higherLevelCompensationPayout: CompensationPayoutForServiceActivity | undefined;

                        if (higherLevelCompensationPayouts) {
                            higherLevelCompensationPayout = findHigherLevelCompensationPayout(
                                higherLevelCompensationPayouts.serviceActivityCompensationPayouts,
                                serviceActivity.id,
                                'serviceOrganizationSettingId'
                            );

                            const groupCompensationPayout = findHigherLevelCompensationPayout(
                                higherLevelCompensationPayouts.serviceActivityCompensationPayouts,
                                serviceActivity.id,
                                'serviceGroupSettingId'
                            );

                            if (groupCompensationPayout) {
                                higherLevelCompensationPayout = groupCompensationPayout;
                            }
                        }

                        return (
                            <ServiceActivityCompensationPayout
                                canEdit={canEdit}
                                compensationPayouts={compensationPayoutsForActivity}
                                episodeCategory={episodeCategory}
                                higherLevelCompensationPayout={higherLevelCompensationPayout}
                                key={serviceActivity.id}
                                onSubmit={onSubmit}
                                organizationId={organizationId}
                                rightEndDate={serviceEmployment.endDate}
                                rightStartDate={serviceEmployment.startDate}
                                serviceActivityId={serviceActivity.id}
                                serviceEmploymentId={serviceEmployment.id}
                                serviceId={serviceEmployment.service.id}
                                title={title}
                            />
                        );
                    });
                })}

            {!employmentId &&
                organizationId &&
                serviceOrganizationSettings.map((serviceOrganizationSetting) => {
                    const uniqueServiceActivities = uniqBy(
                        serviceEmployments.map((se) => se.serviceActivities).flat(),
                        'id'
                    );

                    return uniqueServiceActivities.map((serviceActivity) => {
                        const title = `${serviceActivity.name} (${DateTime.from(
                            serviceOrganizationSetting.startDate
                        ).format(
                            'DD-MM-YYYY'
                        )} - ${serviceOrganizationSetting.endDate ? DateTime.from(serviceOrganizationSetting.endDate).format('DD-MM-YYYY') : translate.common.now.toLowerCase()})`;
                        const canEdit = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);
                        const compensationPayoutsForActivity = compensationPayouts.filter(
                            (compensationPayout) =>
                                compensationPayout.serviceActivityId === serviceActivity.id &&
                                compensationPayout.serviceOrganizationSettingId === serviceOrganizationSetting.id
                        );

                        let higherLevelCompensationPayout: CompensationPayoutForServiceActivity | undefined;

                        if (higherLevelCompensationPayouts) {
                            higherLevelCompensationPayout = findHigherLevelCompensationPayout(
                                higherLevelCompensationPayouts.serviceActivityCompensationPayouts,
                                serviceActivity.id,
                                'serviceGroupSettingId'
                            );
                        }

                        return (
                            <ServiceActivityCompensationPayout
                                canEdit={canEdit}
                                compensationPayouts={compensationPayoutsForActivity}
                                episodeCategory={episodeCategory}
                                higherLevelCompensationPayout={higherLevelCompensationPayout}
                                key={serviceActivity.id}
                                onSubmit={onSubmit}
                                organizationId={organizationId}
                                rightEndDate={serviceOrganizationSetting.endDate}
                                rightStartDate={serviceOrganizationSetting.startDate}
                                serviceActivityId={serviceActivity.id}
                                serviceId={serviceOrganizationSetting.service.id}
                                serviceOrganizationSettingId={serviceOrganizationSetting.id}
                                title={title}
                            />
                        );
                    });
                })}
        </>
    );

    function findHigherLevelCompensationPayout(
        higherLevelServiceActivityCompensationPayouts: CompensationPayoutForServiceActivity[],
        serviceActivityId: string,
        entity: 'serviceOrganizationSettingId' | 'serviceGroupSettingId'
    ) {
        return higherLevelServiceActivityCompensationPayouts.find(
            (compensationPayout) =>
                compensationPayout.serviceActivityId === serviceActivityId &&
                compensationPayout[entity] &&
                (compensationPayout.endDate === null ||
                    DateTime.from(compensationPayout.endDate).isSameOrAfter(DateTime.now()))
        );
    }
}
